import React from "react";
//#region redux
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store";
import { ThunkDispatch } from "redux-thunk";
import { closeDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { CommonConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useCashReconciliationPermissions, useCreateCashReconciliationDialog } from "components/cashReconciliation";
import { useCreateRevenueReport } from "components/revenueReporting";
import { Icon, MenuButton, RoundButton, useCheckRelations } from "components/common";
import { useCreateDocumentDialog, useGenericDocumentPermissions } from "components/documents";
import { useCreateInvoice, useInvoicePermissions } from "components/invoice";
import { useCreateAllowanceDialog, useCreateLeaveDialog } from "components/salaryDeviation";
import { useWintDeviationCreateDialog, useEarlyPayoutDialog, useEarlyPayoutPermissions } from "components/wintSalary";
import {
  useOldSupporDialogHelper,
  useSupportTicketCreateDialog,
  useSupportTicketCreateDialogWithFAQ,
} from "components/support";
import { WelcomeGuideTooltip } from "components/welcomeGuide";
import { useCreateQuotation, useQuotationPermissions } from "components/quotation";
import { useCreateWeeklyTimeReport } from "components/timeReporting";
import { useCreateVoucherDialog, useVoucherPermissions } from "components/voucher";
import { useCompanyService } from "serviceHooks";
import { useIncomingInvoicePermissions } from "components/incomingInvoice";
import { useReceiptPermissions } from "components/receipt";
import { useSalaryPermissions } from "components/salary";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SpeedDial: React.FC = () => {
  const companyService = useCompanyService();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const { getIsReadonlyUser } = useCheckRelations();
  const isReadonlyUser = getIsReadonlyUser();

  const { hasOldSupportDialog } = useOldSupporDialogHelper();
  const { getDialog: getSupportTicketCreateDialog } = useSupportTicketCreateDialog();
  const supportTicketDialogWithFAQ = useSupportTicketCreateDialogWithFAQ(undefined, () => dispatch(closeDialog()));
  const supportCategoryOptions = useSelector((state: AppState) => state.common.supportCategoryOptions);
  const isAgencyMember =
    supportCategoryOptions.agencyMode &&
    (supportCategoryOptions.recipientOptions ?? []).includes(WintSuperkollAPIModel.SupportIssueRecipient.Customer);
  //#region salary
  const createLeaveDialog = useCreateLeaveDialog();
  const createAllowanceDialog = useCreateAllowanceDialog();
  const createWintDeviationDialog = useWintDeviationCreateDialog();
  const { getEarlyPayoutDialog } = useEarlyPayoutDialog();
  const { isSalaryDeviationsAvailable, isWintSalaryDeviationsAvailable } = useSalaryPermissions();
  const { canCreateEarlyPayout } = useEarlyPayoutPermissions();
  const isTimeReportingEnabled = companyService.canLoggedInUserReportTime();
  //#endregion
  //#region document
  const { receiptCreator } = useReceiptPermissions();
  const { canCreateGenericDocuments } = useGenericDocumentPermissions();
  const { canCreateIncomingInvoices } = useIncomingInvoicePermissions();
  const createDocumentDialog = useCreateDocumentDialog(undefined, true);
  const isDocumentCreationApplicable = receiptCreator || canCreateIncomingInvoices || canCreateGenericDocuments;
  //#endregion
  //#region cash reconciliation
  const cashReconciliationDialog = useCreateCashReconciliationDialog();
  const onCreateRevenueReport = useCreateRevenueReport(cashReconciliationDialog);
  const { canCreateCashReconciliations: hasCreatePermission } = useCashReconciliationPermissions();
  //#endregion
  //#region voucher
  const createVoucherDialog = useCreateVoucherDialog();
  const { getCanBookVouchers } = useVoucherPermissions();
  const canBookVouchers = getCanBookVouchers();
  //#endregion
  //#region customer invoices
  const onCreateInvoice = useCreateInvoice();
  const { canUseInvoices } = useInvoicePermissions();
  //#endregion
  //#region quotations
  const { canCreateQuotations } = useQuotationPermissions();
  //#endregion

  const onCreateQuotation = useCreateQuotation();
  const onCreateWeeklyTimeReport = useCreateWeeklyTimeReport();
  const shownHelps = useSelector((state: AppState) =>
    state.auth.profileToggles.shownHelps?.includes(CommonConstants.WelcomeGuideId)
  );
  const shownIntroductions = useSelector((state: AppState) =>
    state.auth.profileToggles.shownIntroductions?.includes(CommonConstants.WelcomeGuideId)
  );

  const speedDialButton = (
    <RoundButton
      size="lg"
      hoverText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Submit)}
      hoverTextAsToolTip
      className={cssExports.menuButton}
    >
      <Icon icon={IconDefinitionConstants.WPlus} />
    </RoundButton>
  );

  const speedDialButtonWrap = () => {
    return shownIntroductions && !shownHelps ? (
      <div>
        <WelcomeGuideTooltip
          open={shownIntroductions && !shownHelps}
          placement="top-end"
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SpeedDialHelpText)}
          content={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SpeedDialContentHelpText)}
        >
          {speedDialButton}
        </WelcomeGuideTooltip>
      </div>
    ) : (
      speedDialButton
    );
  };

  return (
    <MenuButton
      button={speedDialButtonWrap()}
      classes={{
        root: cssExports.speedDialOuter,
        menuOpen: cssExports.speedDialOpen,
        menuClosing: cssExports.speedDialClosing,
      }}
      menuPlacement={"top"}
      blurBackgroundOnOpen
      popperOffSet={[10, 20]}
      menuItems={[
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.AddDocument),
          icon: IconDefinitionConstants.WDocument,
          actionDialog: createDocumentDialog,
          hidden: isReadonlyUser || !isDocumentCreationApplicable,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.AddDeviation),
          icon: IconDefinitionConstants.WCalendar,
          actionDialog: createWintDeviationDialog(),
          hidden: isReadonlyUser || !isWintSalaryDeviationsAvailable,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutMenuItemText),
          icon: IconDefinitionConstants.WMoney,
          actionDialog: getEarlyPayoutDialog(),
          hidden: isReadonlyUser || !isWintSalaryDeviationsAvailable || !canCreateEarlyPayout,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.AddTimeDeviation),
          icon: IconDefinitionConstants.WCalendar,
          actionDialog: createLeaveDialog(),
          hidden: isReadonlyUser || !isSalaryDeviationsAvailable,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.CreateAllowance),
          icon: IconDefinitionConstants.WMoney,
          actionDialog: createAllowanceDialog(),
          hidden: isReadonlyUser || !isSalaryDeviationsAvailable,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SupportIssue.FAQSupportFlowAskAQuestion
          ),
          icon: IconDefinitionConstants.WCommentDots,
          actionDialog: getSupportTicketCreateDialog(),
          hidden: isAgencyMember || !hasOldSupportDialog,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SupportIssue.FAQSupportFlowAskAQuestion
          ),
          icon: IconDefinitionConstants.WCommentDots,
          actionDialog: supportTicketDialogWithFAQ,
          hidden: isAgencyMember || hasOldSupportDialog,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SendMessage),
          icon: IconDefinitionConstants.WCommentDots,
          actionDialog: getSupportTicketCreateDialog(undefined, undefined, undefined, true),
          hidden: !isAgencyMember,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SubmitDailyTakings),
          icon: IconDefinitionConstants.WCalculator,
          hidden: isReadonlyUser || !hasCreatePermission(),
          onClickHandler: onCreateRevenueReport,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CreateCustomerInvoice),
          icon: IconDefinitionConstants.WPaperPlane,
          hidden: isReadonlyUser || !canUseInvoices,
          onClickHandler: onCreateInvoice,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.CreateQuotation),
          icon: IconDefinitionConstants.WPaperPlane,
          onClickHandler: onCreateQuotation,
          hidden: !canCreateQuotations,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.TimeReport),
          icon: IconDefinitionConstants.WTimer,
          onClickHandler: onCreateWeeklyTimeReport,
          hidden: isReadonlyUser || !isTimeReportingEnabled,
        },
        {
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Voucher.CreateVoucher),
          icon: IconDefinitionConstants.WDocument,
          hidden: isReadonlyUser || !canBookVouchers,
          actionDialog: createVoucherDialog,
        },
      ]}
    />
  );
};

export default SpeedDial;
